/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The cross-household conversation on a recipe.
 *
 * ── THE ONE THING THIS UI MUST GET RIGHT ────────────────────────────────────
 * A user must never be confused about which box is which. The recipe already
 * has private family notes; this panel looks different, is labelled differently,
 * and restates who can read it in the panel itself rather than in a tooltip —
 * because someone who joined months ago will not remember what this box was.
 *
 * The recipes themselves are still copies. Nothing here refetches another
 * household's recipe; the only live thing is the conversation.
 */

import { useState } from "react";

import { ThreadsApi } from "../../api/endpoints.ts";
import type { ThreadAvailability, ThreadComment } from "../../api/types.ts";
import { Banner, ErrorBox, Loading, Modal, Pill } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { useThreadSync } from "./useThreadSync.ts";

export function RecipeThreadPanel({ recipeId, state, onStateChange }: {
  recipeId: string;
  /** From `ThreadsApi.state`, fetched alongside the recipe. */
  state: ThreadAvailability;
  /** Refetch the state after joining or leaving. */
  onStateChange: () => void;
}) {
  // No discussion-enabled share ever touched this recipe. Render nothing at
  // all rather than an empty affordance the user can't act on.
  if (state.state === "unavailable") return null;

  if (state.state === "available" || state.state === "left") {
    return <JoinPrompt recipeId={recipeId} left={state.state === "left"} onJoined={onStateChange} />;
  }
  return <LiveThread recipeId={recipeId} onLeft={onStateChange} />;
}

// ─────────────────────────────────────────────────────────────────────────────

function JoinPrompt({ recipeId, left, onJoined }: {
  recipeId: string; left: boolean; onJoined: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function join() {
    setBusy(true);
    try {
      await ThreadsApi.join(recipeId);
      onJoined();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't join the discussion.", "danger");
    }
    setBusy(false);
  }

  return (
    <section className="card p-5">
      <h3 className="font-medium text-ink">
        {left ? "You left this discussion" : "Other families are cooking this too"}
      </h3>
      <p className="mt-1 text-sm text-ink-2">
        {left
          ? "You can rejoin whenever you like — your old comments are still there."
          : "The family who shared this recipe opened it for discussion. Joining lets " +
            "you read and add notes that every family with this recipe can see."}
      </p>
      {/* Stated before the click, not after it. */}
      <p className="mt-2 text-xs text-ink-3">
        Your family's notes, ratings and meal plans stay private either way. Only what
        you write in the discussion is shared.
      </p>
      <button type="button" onClick={join} disabled={busy} className="btn-primary mt-4">
        {busy ? "Joining…" : left ? "Rejoin discussion" : "Join discussion"}
      </button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LiveThread({ recipeId, onLeft }: { recipeId: string; onLeft: () => void }) {
  const toast = useToast();
  const sync = useThreadSync(recipeId, true);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<ThreadComment | null>(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  if (sync.revoked) {
    return <Banner tone="warn">You're no longer part of this discussion.</Banner>;
  }
  if (sync.loading && !sync.summary) return <Loading label="Loading discussion…" />;
  if (sync.error && !sync.summary) {
    return <ErrorBox message={sync.error} onRetry={sync.refresh} />;
  }

  const s = sync.summary!;

  async function run(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    try {
      await fn();
      sync.refresh();
      if (ok) toast(ok, "ok");
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work.", "danger");
    }
    setBusy(false);
  }

  async function leave(deleteMyComments: boolean) {
    try {
      await ThreadsApi.leave(recipeId, deleteMyComments);
      setLeaving(false);
      onLeft();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't leave.", "danger");
    }
  }

  return (
    <section className="card">
      <header className="flex flex-wrap items-center gap-2 border-b border-rule-soft px-5 py-3">
        <h3 className="font-medium text-ink">Family discussion</h3>
        <Pill>{s.participantCount} famil{s.participantCount === 1 ? "y" : "ies"}</Pill>
        {s.isOrigin && <Pill tone="accent">You shared this</Pill>}
        {s.isLocked && <Pill tone="warn">Closed</Pill>}
        {s.rating.count > 0 && <Pill tone="ok">★ {s.rating.average} · {s.rating.count}</Pill>}

        <div className="ml-auto flex items-center gap-3 text-xs text-ink-3">
          {s.isOrigin && (
            <button type="button" className="hover:text-ink"
              onClick={() => run(() => ThreadsApi.lock(recipeId, !s.isLocked))}>
              {s.isLocked ? "Reopen" : "Close discussion"}
            </button>
          )}
          <button type="button" className="hover:text-ink" onClick={() => setLeaving(true)}>
            Leave
          </button>
        </div>
      </header>

      {/* Repeated on the panel, not only at the join step. */}
      <p className="border-b border-rule-soft px-5 py-2 text-xs text-ink-3">
        Visible to every family that has this recipe. Your family's own notes are in
        the panel above and stay private.
      </p>

      {s.isLocked && s.lockedReason && (
        <div className="px-5 pt-4"><Banner tone="warn">{s.lockedReason}</Banner></div>
      )}

      <StarRow
        recipeId={recipeId}
        mine={s.rating.mine}
        onRated={sync.refresh}
      />

      <ol className="divide-y divide-rule-soft">
        {sync.comments.length === 0 && (
          <li className="px-5 py-8 text-center text-sm text-ink-3">
            Nothing yet. Say how it went.
          </li>
        )}
        {sync.comments.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            parent={sync.comments.find((p) => p.id === c.replyTo) ?? null}
            canModerate={s.isOrigin}
            onReply={() => setReplyTo(c)}
            onRemove={() => run(() => ThreadsApi.deleteComment(recipeId, c.id))}
            onFlag={() => run(
              () => ThreadsApi.flagComment(recipeId, c.id),
              "Reported to the family who shared this recipe.",
            )}
            onBlock={() => run(
              () => ThreadsApi.block(recipeId, c.participantId, !c.hiddenByBlock),
            )}
          />
        ))}
      </ol>

      {!s.isLocked && (
        <div className="border-t border-rule-soft p-4">
          {replyTo && (
            <div className="mb-2 flex items-center gap-2 text-xs text-ink-3">
              <span>Replying to {replyTo.authorLabel}</span>
              <button type="button" className="underline" onClick={() => setReplyTo(null)}>
                cancel
              </button>
            </div>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder="How did it go? Anything you'd change?"
            className="input w-full resize-y"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-ink-3">{draft.length}/2000</span>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !draft.trim()}
              onClick={async () => {
                const body = draft.trim();
                if (!body) return;
                await run(async () => {
                  await ThreadsApi.comment(recipeId, body, replyTo?.id ?? null);
                  setDraft("");
                  setReplyTo(null);
                });
              }}
            >
              Post
            </button>
          </div>
        </div>
      )}

      {leaving && (
        <Modal title="Leave this discussion?" onClose={() => setLeaving(false)}>
          <p className="text-sm text-ink-2">
            You'll keep your copy of the recipe, and you can rejoin later.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" className="btn-ghost" onClick={() => leave(false)}>
              Leave, and keep my comments
            </button>
            {/* Second, and worded as the destructive act it is — the other
                families were part of that conversation too. */}
            <button type="button" className="btn-danger" onClick={() => leave(true)}>
              Leave and delete everything I wrote
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The cross-household rating: one vote per FAMILY, because the family is what
 * cooked the dish. The private per-member rating on the recipe is untouched.
 */
function StarRow({ recipeId, mine, onRated }: {
  recipeId: string; mine: number | null; onRated: () => void;
}) {
  const toast = useToast();
  return (
    <div className="flex items-center gap-2 border-b border-rule-soft px-5 py-3 text-sm">
      <span className="text-ink-3">Your family's rating</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className={n <= (mine ?? 0) ? "text-accent" : "text-ink-3 hover:text-ink-2"}
          onClick={async () => {
            try {
              await ThreadsApi.rate(recipeId, n);
              onRated();
            } catch (e) {
              toast(e instanceof Error ? e.message : "Couldn't save that.", "danger");
            }
          }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function CommentRow({ comment: c, parent, canModerate, onReply, onRemove, onFlag, onBlock }: {
  comment: ThreadComment;
  parent: ThreadComment | null;
  canModerate: boolean;
  onReply: () => void;
  onRemove: () => void;
  onFlag: () => void;
  onBlock: () => void;
}) {
  // A blocked family's text is withheld by the SERVER (body is null), not just
  // hidden with CSS. The row stays so the block is visible and reversible.
  if (c.hiddenByBlock) {
    return (
      <li className="px-5 py-2 text-xs italic text-ink-3">
        Comment from {c.authorLabel} hidden.{" "}
        <button type="button" className="underline" onClick={onBlock}>Show</button>
      </li>
    );
  }

  return (
    <li className={`px-5 py-3 ${c.isOwnHousehold ? "bg-paper-3/50" : ""}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium text-ink">{c.authorLabel}</span>
        {c.isOwnHousehold && <Pill tone="accent">Your family</Pill>}
        <span className="text-xs text-ink-3">
          {new Date(c.createdAt).toLocaleDateString()}{c.editedAt && " · edited"}
        </span>
        {canModerate && c.flagCount ? (
          <Pill tone="warn">{c.flagCount} report{c.flagCount === 1 ? "" : "s"}</Pill>
        ) : null}
      </div>

      {parent && (
        <p className="mt-1 border-l-2 border-rule-soft pl-2 text-xs text-ink-3">
          {parent.authorLabel}: {(parent.body ?? "").slice(0, 100)}
        </p>
      )}

      <p className="mt-1 whitespace-pre-wrap text-sm text-ink-2">{c.body}</p>

      <div className="mt-1 flex flex-wrap gap-3 text-xs text-ink-3">
        <button type="button" className="hover:text-ink" onClick={onReply}>Reply</button>
        {(c.isMine || canModerate) && (
          <button type="button" className="hover:text-danger" onClick={onRemove}>Delete</button>
        )}
        {!c.isOwnHousehold && (
          <>
            <button type="button" className="hover:text-ink" onClick={onFlag}>Report</button>
            <button type="button" className="hover:text-ink" onClick={onBlock}>
              Hide this family
            </button>
          </>
        )}
      </div>
    </li>
  );
}
